import React, { useState, useEffect, useCallback } from 'react';
import patientService from '../../../../../services/patient.service';
import Pagination from '../../../../common/Pagination';
import { formatPatientCount } from '../../subparts/patientFilesQuery';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import { showError } from '../../../../../utils/ecxSwal';
import { PatientAppointmentsSkeleton } from '../subparts/PatientTabSkeletons';
import { getAppointmentStatusMeta, getReminderItems, REMINDER_SENT_TYPES } from '../../../appointments/subparts/appointmentsQuery';
import PatientDetailIcon from '../subparts/PatientDetailIcon';

const PAGE_SIZE = 10;

const PatientAppointments = ({ patient }) => {
  const [loading, setLoading] = useState(true);
  const [pageNo, setPageNo] = useState(0);
  const clinicId = useEyeconxStore((state) => state.user.clinicId);
  const [appointmentsData, setAppointmentsData] = useState(null);

  const getAppointments = useCallback(async (nextPage = 0) => {
    if (!clinicId || !patient?.id) {
      return;
    }

    try {
      setLoading(true);
      const response = await patientService.getPatientAppointments(
        clinicId,
        patient.id,
        nextPage,
        PAGE_SIZE
      );
      setAppointmentsData(
        response?.data || { content: [], totalElements: 0, number: 0, totalPages: 1, size: PAGE_SIZE }
      );
      setPageNo(nextPage);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      showError({ title: 'Load Failed', text: 'Failed to load appointments.' });
      setAppointmentsData({ content: [], totalElements: 0, number: 0, totalPages: 1, size: PAGE_SIZE });
    } finally {
      setLoading(false);
    }
  }, [clinicId, patient?.id]);

  useEffect(() => {
    getAppointments(0);
  }, [getAppointments]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const appointments = appointmentsData?.content || [];
  const totalElements = appointmentsData?.totalElements || 0;
  const currentPage = appointmentsData?.number ?? pageNo;
  const totalPages = appointmentsData?.totalPages || 1;

  if (!appointmentsData || loading) {
    return <PatientAppointmentsSkeleton />;
  }

  return (
    <div className="patient-detail-tab-panel">
      <div className="patient-detail-tab-panel__header">
        <div>
          <h3 className="patient-detail-tab-panel__title">
            <PatientDetailIcon icon="bi-calendar-check" theme="purple" size="sm" />
            Appointments
            <span className="ecx-page-chip ms-2">{formatPatientCount(totalElements)}</span>
          </h3>
          <p className="patient-detail-tab-panel__subtitle">
            Upcoming and past appointments for this patient.
          </p>
        </div>
      </div>

      <div className="patient-detail-table-wrap">
        {appointments.length === 0 ? (
          <div className="patient-detail-empty-state">
            <PatientDetailIcon icon="bi-calendar-x" theme="slate" size="lg" />
            <p>No appointments found</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0 patient-detail-table">
              <thead>
                <tr>
                  <th className="ps-4">Date & Time</th>
                  <th>Doctor</th>
                  <th>Patient</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Reminders</th>
                </tr>
              </thead>

              <tbody>
                {appointments.map((appointment) => {
                  const statusMeta = getAppointmentStatusMeta(appointment?.isConfirmed);
                  const apptDate = new Date(appointment?.dateAppointment);
                  const isToday = new Date().toDateString() === apptDate.toDateString();
                  const doctor = appointment?.doctor || {};
                  const pat = appointment?.patient || {};
                  const reminderItems = getReminderItems(appointment?.appointmentStatus || {});

                  return (
                    <tr key={appointment.id}>
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                          {isToday && <span className="badge bg-warning me-2">Today</span>}
                          <div>
                            <div className="fw-semibold">{formatDate(appointment.dateAppointment)}</div>
                            <small className="text-muted">
                              <i className="bi bi-clock me-1" />
                              {appointment.startTime} - {appointment.endTime}
                            </small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="fw-medium">
                          Dr. {doctor.firstName} {doctor.lastName}
                        </div>
                      </td>

                      <td>
                        <div className="fw-medium">
                          {pat.firstName} {pat.lastName}
                          {pat.preferredName && (
                            <small className="text-muted ms-1">({pat.preferredName})</small>
                          )}
                        </div>
                        <small className="text-muted">
                          <i className="bi bi-telephone me-1" />
                          {pat.phoneNumber || '—'}
                        </small>
                      </td>

                      <td>
                        <span className="text-muted">15 mins</span>
                      </td>

                      <td>
                        <span className={`badge bg-${statusMeta.variant}`}>
                          <i className={`bi ${statusMeta.icon} me-1`} />
                          {statusMeta.label}
                        </span>
                      </td>

                      <td>
                        <div className="d-flex gap-2">
                          {reminderItems
                            .filter((item) => item.active)
                            .map((item) => {
                              const reminderIcon = item.channelIcon;

                              return (
                                <i
                                  key={item.label}
                                  className={`bi ${reminderIcon.icon} text-${reminderIcon.colorClass}`}
                                  title={
                                    item.date
                                      ? `${item.label} reminder sent on ${item.date} via ${
                                            REMINDER_SENT_TYPES[item.type] || 'Unknown'
                                        }`
                                      : `${item.label} reminder not sent`
                                  }
                                />
                              );
                            })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="patient-detail-pagination">
          <div className="text-muted small">
            Showing {formatPatientCount(appointments.length)} of {formatPatientCount(totalElements)} appointments
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={getAppointments}
            totalElements={totalElements}
            pageSize={appointmentsData?.size || PAGE_SIZE}
          />
        </div>
      )}
    </div>
  );
};

export default PatientAppointments;
